import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { UserListResponseDto } from '@bandido/shared/dtos';
import { Channels } from '@bandido/shared/constants';

interface AutocompleteMail extends Pick<UserListResponseDto, 'id' | 'email'> {
  firstName?: string;
  lastName?: string;
}

export type EditorContextState = {
  isOpen: boolean;
  showCc: boolean;
  showBcc: boolean;
  channel: Channels | null;
  from: string;
  historyIdToReply: number | null;
  subject: string;
  to: AutocompleteMail[];
  cc?: AutocompleteMail[];
  bcc?: AutocompleteMail[];
  editorContent: string | undefined;
  setHistoryIdToReply: React.Dispatch<React.SetStateAction<number | null>>;
  setChannel: React.Dispatch<React.SetStateAction<Channels | null>>;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onToggleCc: () => void;
  onToggleBcc: () => void;
  setSubject: React.Dispatch<React.SetStateAction<string>>;
  setFrom: React.Dispatch<React.SetStateAction<string>>;
  setTo: React.Dispatch<React.SetStateAction<AutocompleteMail[]>>;
  setCc: React.Dispatch<React.SetStateAction<AutocompleteMail[]>>;
  setBcc: React.Dispatch<React.SetStateAction<AutocompleteMail[]>>;
  setEditorContent: React.Dispatch<React.SetStateAction<string | undefined>>;
};

export const EditorContext = createContext<EditorContextState>({
  subject: '',
  from: '',
  to: [],
  cc: [],
  bcc: [],
  isOpen: false,
  channel: null,
  historyIdToReply: null,
  showCc: false,
  showBcc: false,
  editorContent: undefined,
  onToggleCc: () => {},
  onToggleBcc: () => {},
  setIsOpen: () => {},
  setChannel: () => {},
  setTo: () => {},
  setCc: () => {},
  setFrom: () => {},
  setBcc: () => {},
  setSubject: () => {},
  setHistoryIdToReply: () => {},
  setEditorContent: () => {},
});

const EditorContextProvider = ({ children }: React.PropsWithChildren) => {
  const [subject, setSubject] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState<AutocompleteMail[]>([]);
  const [cc, setCc] = useState<AutocompleteMail[]>([]);
  const [bcc, setBcc] = useState<AutocompleteMail[]>([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [editorContent, setEditorContent] = useState<string | undefined>('');

  const onToggleCc = useCallback(() => setShowCc((prev) => !prev), []);
  const onToggleBcc = useCallback(() => setShowBcc((prev) => !prev), []);

  const [isOpen, setIsOpen] = useState(false);
  const [channel, setChannel] = useState<Channels | null>(null);
  const [historyIdToReply, setHistoryIdToReply] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSubject('');
      setTo([]);
      setBcc([]);
      setFrom('');
      setEditorContent('');
      setCc([]);
      setShowCc(false);
      setShowBcc(false);
      setHistoryIdToReply(null);
    }
  }, [isOpen]);

  const value = useMemo(
    () => ({
      channel,
      subject,
      isOpen,
      historyIdToReply,
      from,
      to,
      cc,
      bcc,
      showCc,
      showBcc,
      editorContent,
      onToggleCc,
      onToggleBcc,
      setTo,
      setSubject,
      setCc,
      setFrom,
      setBcc,
      setIsOpen,
      setChannel,
      setHistoryIdToReply,
      setEditorContent,
    }),
    [subject, to, cc, bcc, isOpen, channel, historyIdToReply, showCc, showBcc, editorContent],
  );

  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
};

export const useEditor = () => React.useContext(EditorContext);

export default EditorContextProvider;
